import { LocalizationStore } from "@faire/web--source/common/localization/LocalizationStore";
import { getSanitizeArticleFunc } from "@faire/web--source/common/zendesk/utils";
import { getLocalePrefix } from "@faire/web--source/ui/routing/util";
import { IZendeskArticle } from "@faire/web-api--source/com/faire/vendor/zendesk/IZendeskArticle";
import flow from "lodash/flow";

import { localizeArticleUrl } from "./localizeArticleUrl";
import {
  removeAllAmpersands,
  removeAllNonBreakingSpaces,
} from "./removeSpecialCharacters";

export const getArticlePreview = (article: IZendeskArticle) => {
  if (!article.body) {
    return "";
  }
  const div = document.createElement("div");
  div.innerHTML = article.body;
  return div.textContent || div.innerText;
};

export const extractArticleSectionsFromBody = (body: string) => {
  const bodyWithoutAmpersands = removeAllAmpersands(body);
  const bodyWithoutNonBreakingSpaces = removeAllNonBreakingSpaces(
    bodyWithoutAmpersands
  );
  return bodyWithoutNonBreakingSpaces
    .match(/<h1\b[^>]*>(.*?)<\/h1>/g)
    ?.map((sectionTitle) => sectionTitle.replace(/<(.*?)>/g, ""))
    .filter(Boolean);
};

export const sanitizeArticle = (body: string) => {
  const locale = LocalizationStore.get().locale;
  const localePrefix = getLocalePrefix(locale).replace("/", "");
  const articleBody = localePrefix
    ? localizeArticleUrl(body, localePrefix)
    : body;
  const sanitizePatterns = {
    "<h1> </h1>": "",
    "<p> </p>": "",
    '"<a': "<a",
    '</a>"': "</a>",
  };

  const sanitize = flow(
    getSanitizeArticleFunc(sanitizePatterns),
    injectOrReplaceWithSequentialIdsToH1,
    injectImageClassName
  );
  return sanitize(articleBody);
};

export const IMAGE_CONTAINER_CLASS_NAME = "imageContainer";

// Injects section ids to tags H1, potentially we could create a generic function accepting any type of tags
const injectOrReplaceWithSequentialIdsToH1 = (htmlText: string): string => {
  // Regular expression to match <h1> tags with or without an id attribute
  const regex = /<h1(?:\s+id=['"][^'"]*['"])?\s*>/gi;
  let index = 0;

  // Replace all matches with the formatted string
  return htmlText.replace(regex, () => {
    const injectedH1 = `<h1 id="${getSectionId(index)}">`;
    index++;
    return injectedH1;
  });
};

// Injects a class for images to match the design
const injectImageClassName = (htmlText: string): string => {
  return replaceAllString(
    htmlText,
    "<p><img",
    () => `<p class=${IMAGE_CONTAINER_CLASS_NAME}><img`
  );
};

export const getSectionId = (index: number) => {
  const sectionIdBase = "sectionTitle";
  return `${sectionIdBase}_${index}`;
};

const replaceAllString = (
  content: string,
  targetString: string,
  replaceCallback: (index: number) => string
) => {
  const contentArray = content.split(targetString);
  return contentArray
    .map(
      (text, index) =>
        `${text}${
          index !== contentArray.length - 1 ? replaceCallback(index) : ""
        }`
    )
    .join("");
};
